/**
 * PortalDecisionRules — Admin page for managing CALL / LEAD / MULTI_OFFER thresholds
 *
 * Visual editor for the decision_rules table. Each rule defines when a particular
 * routing decision triggers based on intent + monetization score thresholds.
 */

import React, { useState, useEffect, useCallback } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { GitBranch, Phone, Mail, Layers, Loader2, Save } from 'lucide-react';
import { supabaseGet, supabasePatch } from '@/utils/supabaseRest';

interface DecisionRule {
  id: string;
  vertical_id: string | null;
  decision: 'CALL' | 'LEAD' | 'MULTI_OFFER';
  conditions: {
    intent_min?: number;
    intent_max?: number;
    monetization_min?: number;
    monetization_max?: number;
  };
  priority: number;
  route_config: Record<string, unknown> | null;
  is_active: boolean;
}

const DECISION_META: Record<string, { label: string; icon: React.ReactNode; color: string; description: string }> = {
  CALL: {
    label: 'Call',
    icon: <Phone className="h-5 w-5" />,
    color: 'emerald',
    description: 'High-intent users routed directly to phone call',
  },
  LEAD: {
    label: 'Lead',
    icon: <Mail className="h-5 w-5" />,
    color: 'blue',
    description: 'Mid-intent users complete a lead form',
  },
  MULTI_OFFER: {
    label: 'Multi-Offer',
    icon: <Layers className="h-5 w-5" />,
    color: 'violet',
    description: 'Lower-intent users see multiple fallback offers',
  },
};

export default function PortalDecisionRules() {
  const [rules, setRules] = useState<DecisionRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [pendingChanges, setPendingChanges] = useState<Record<string, Partial<DecisionRule['conditions']>>>({});

  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      const data = await supabaseGet('decision_rules?order=priority.asc');
      setRules(data || []);
    } catch (err) {
      console.error('Failed to load decision rules:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRules(); }, [loadRules]);

  const updateThreshold = (ruleId: string, field: keyof DecisionRule['conditions'], value: number) => {
    setPendingChanges(prev => ({
      ...prev,
      [ruleId]: { ...(prev[ruleId] || {}), [field]: value },
    }));
  };

  const saveRule = async (rule: DecisionRule) => {
    const changes = pendingChanges[rule.id];
    if (!changes) return;

    setSaving(rule.id);
    try {
      const newConditions = { ...rule.conditions, ...changes };
      await supabasePatch(`decision_rules?id=eq.${rule.id}`, { conditions: newConditions });
      setRules(prev => prev.map(r => r.id === rule.id ? { ...r, conditions: newConditions } : r));
      setPendingChanges(prev => {
        const next = { ...prev };
        delete next[rule.id];
        return next;
      });
    } catch (err) {
      console.error('Failed to save decision rule:', err);
    } finally {
      setSaving(null);
    }
  };

  const getEffectiveValue = (rule: DecisionRule, field: keyof DecisionRule['conditions']): number => {
    return pendingChanges[rule.id]?.[field] ?? rule.conditions[field] ?? 0;
  };

  return (
    <PortalLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <GitBranch className="h-6 w-6 text-emerald-400" />
            Decision Rules
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Set the score thresholds that determine whether a user gets routed to Call, Lead, or Multi-Offer.
          </p>
        </div>

        {/* Visual score bar */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-5">
            <div className="text-xs text-slate-500 mb-2">Intent Score Distribution</div>
            <div className="h-8 rounded-lg overflow-hidden flex">
              {rules.filter(r => r.vertical_id === null).sort((a, b) => a.priority - b.priority).map((rule) => {
                const meta = DECISION_META[rule.decision];
                const min = getEffectiveValue(rule, 'intent_min');
                const max = getEffectiveValue(rule, 'intent_max') || 100;
                const width = max - min + 1;
                const colorMap: Record<string, string> = {
                  emerald: 'bg-emerald-500/40',
                  blue: 'bg-blue-500/40',
                  violet: 'bg-violet-500/40',
                };
                return (
                  <div
                    key={rule.id}
                    className={`${colorMap[meta.color]} flex items-center justify-center text-xs font-medium text-white/80`}
                    style={{ width: `${width}%` }}
                  >
                    {meta.label} ({min}-{max})
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between text-[10px] text-slate-600 mt-1">
              <span>0</span><span>20</span><span>40</span><span>60</span><span>80</span><span>100</span>
            </div>
          </CardContent>
        </Card>

        {/* Rules */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 text-emerald-400 animate-spin" />
          </div>
        ) : (
          <div className="space-y-3">
            {rules.filter(r => r.vertical_id === null).sort((a, b) => a.priority - b.priority).map((rule) => {
              const meta = DECISION_META[rule.decision];
              const hasChanges = !!pendingChanges[rule.id];
              const colorClasses: Record<string, string> = {
                emerald: 'border-emerald-500/30 bg-emerald-500/5',
                blue: 'border-blue-500/30 bg-blue-500/5',
                violet: 'border-violet-500/30 bg-violet-500/5',
              };

              return (
                <Card key={rule.id} className={`${colorClasses[meta.color]} border`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`text-${meta.color}-400`}>{meta.icon}</div>
                        <div>
                          <div className="text-lg font-semibold text-white">{meta.label}</div>
                          <div className="text-xs text-slate-400">{meta.description}</div>
                        </div>
                      </div>
                      {hasChanges && (
                        <Button
                          size="sm"
                          onClick={() => saveRule(rule)}
                          disabled={saving === rule.id}
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          {saving === rule.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
                          Save
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-slate-400 block mb-1">Intent Score Min</label>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={getEffectiveValue(rule, 'intent_min')}
                          onChange={(e) => updateThreshold(rule.id, 'intent_min', parseInt(e.target.value) || 0)}
                          className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 block mb-1">Intent Score Max</label>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={getEffectiveValue(rule, 'intent_max') || ''}
                          onChange={(e) => updateThreshold(rule.id, 'intent_max', parseInt(e.target.value) || undefined as any)}
                          placeholder="No max"
                          className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-600"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 block mb-1">Monetization Score Min</label>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={getEffectiveValue(rule, 'monetization_min') || ''}
                          onChange={(e) => updateThreshold(rule.id, 'monetization_min', parseInt(e.target.value) || undefined as any)}
                          placeholder="No min"
                          className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-600"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 block mb-1">Monetization Score Max</label>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={getEffectiveValue(rule, 'monetization_max') || ''}
                          onChange={(e) => updateThreshold(rule.id, 'monetization_max', parseInt(e.target.value) || undefined as any)}
                          placeholder="No max"
                          className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-600"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
